import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styles from '@/styles/form.module.css';
import type { Club, Player, PlayerForm } from '@iziping/common';
import { AgeCategory, Sex } from '@iziping/common';
import { useNotification } from '@iziping/ui';
import { useTranslation } from 'react-i18next';
import type { ApiError } from '@iziping/common';
import { useGetPlayer, useSavePlayer } from './playerHooks';
import { AutoCompleteInput } from '../shared/AutoCompleteInput';
import { apiClient } from '@iziping/common';
import { ENDPOINTS } from '@/model/endpoints';
import { Loader2, RotateCw } from 'lucide-react';
import LanguageSelector from '../shared/LanguageSelector';

const fetchUserByLicence = async (licence: string): Promise<Player> => {
    const response = await apiClient.get(ENDPOINTS.synchroFFTT(licence));
    return response.data;
};

const PlayerPage: React.FC = () => {
    const { id } = useParams();
    const playerId = id ? parseInt(id, 10) : undefined;
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { notify } = useNotification();

    const [isSyncing, setIsSyncing] = useState(false);

    const { data: player, isLoading, isError } = useGetPlayer(playerId);
    const saveMutation = useSavePlayer();

    // Formulaire RHF
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        formState: { errors },
    } = useForm<PlayerForm>({
        defaultValues: {
            firstname: '',
            lastname: '',
            points: 500,
            email: '',
            licence: null,
            age: AgeCategory.SENIOR,
            sex: Sex.MASCULIN,
            clubId: null,
            language: 'fr',
        },
    });

    const licence = watch('licence');

    const handleLicenceSync = async () => {
        if (!licence || licence.trim().length < 4) return;

        try {
            setIsSyncing(true);

            const player = await fetchUserByLicence(licence);

            setValue('licence', player.licence);
            setValue('firstname', player.firstname);
            setValue('lastname', player.lastname);
            setValue('clubId', player?.club?.id ?? null);
            setSelectedClub(player?.club ?? null);
            setValue('points', player?.points ?? 500);
            setValue('age', player?.age);
            setValue('sex', player?.sex);
            setValue('email', player.email);

            notify('Données FFTT importées', 'success');
        } catch (error) {
            notify('Licence introuvable', 'error');
            console.error('Error calling api:', error);
        } finally {
            setIsSyncing(false);
        }
    };

    // Autocomplete club
    const [searchText, setSearchText] = useState('');
    const [selectedClub, setSelectedClub] = useState<Club | null>(null);

    // Pré-remplir le formulaire en mode édition
    useEffect(() => {
        if (player) {
            reset({
                firstname: player.firstname,
                lastname: player.lastname,
                points: player.points,
                email: player.email,
                licence: player.licence,
                age: player.age,
                sex: player.sex,
                clubId: player.club?.id,
                language: player.language ?? 'fr',
            });
            if (player.club) {
                setSelectedClub(player.club);
                setSearchText(player.club.name);
            }
        } else {
            reset({
                firstname: '',
                lastname: '',
                points: 500,
                email: '',
                licence: null,
                age: AgeCategory.SENIOR,
                sex: Sex.MASCULIN,
                clubId: null,
            });
            setSelectedClub(null);
            setSearchText('');
        }
    }, [player, reset]);

    if (isLoading) return <div>Chargement...</div>;
    if (isError) return <div>Erreur lors du chargement du joueur</div>;

    const onSubmit = async (data: PlayerForm) => {
        try {
            //const payload = player ? { ...data, id: player.id } : data;
            const payload = player
                ? { id: player.id, data } // édition
                : { data };
            await saveMutation.mutateAsync(payload);
            notify(t('player.created.success'), 'success');
            navigate('/players');
        } catch (err: unknown) {
            const apiError = err as ApiError;
            notify(apiError?.error || 'Erreur inconnue', 'error');
        }
    };

    return (
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <h2 className={styles.title}>{player ? t('player.update') : t('player.create')}</h2>

            <div className={styles.formGroup}>
                <div className={styles.inputWrapper}>
                    <input
                        id="licence"
                        type="text"
                        className={styles.input}
                        {...register('licence', { setValueAs: (v) => (v === '' ? null : v) })}
                    />
                    <button
                        type="button"
                        onClick={handleLicenceSync}
                        className={styles.refreshButton}
                        disabled={!licence || isSyncing}
                    >
                        {isSyncing ? <Loader2 size={16} className={styles.spin} /> : <RotateCw size={16} />}
                    </button>
                </div>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="firstname">{t('player.firstname')}</label>
                <input
                    id="firstname"
                    className={styles.input}
                    {...register('firstname', { required: t('player.firstname.required') })}
                />
                {errors.firstname && <span className={styles.error}>{errors.firstname.message}</span>}
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="lastname">{t('player.lastname')}</label>
                <input
                    id="lastname"
                    className={styles.input}
                    {...register('lastname', { required: t('player.lastname.required') })}
                />
                {errors.lastname && <span className={styles.error}>{errors.lastname.message}</span>}
            </div>

            <AutoCompleteInput
                label={t('player.club')}
                value={selectedClub}
                onChange={(c) => {
                    setSelectedClub(c);
                    setValue('clubId', c?.id ?? null); // RHF : number | null
                }}
                searchFn={async (term) => {
                    const response = await apiClient.get<Club[]>(ENDPOINTS.clubsSearch(term));
                    return response.data;
                }}
                renderOption={(c) => c.name}
                displayValue={(c) => c?.name || ''}
            />

            <div className={styles.formGroup}>
                <label htmlFor="points">{t('player.points')}</label>
                <input id="points" type="number" className={styles.input} {...register('points')} />
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="age">{t('player.age')}</label>
                <select id="age" className={styles.input} {...register('age')}>
                    {Object.values(AgeCategory).map((a) => (
                        <option key={a} value={a}>
                            {t(`AGECATEGORY.${a}`)}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="sex">{t('player.sex')}</label>
                <select id="sex" className={styles.input} {...register('sex')}>
                    {Object.values(Sex).map((s) => (
                        <option key={s} value={s}>
                            {t(`SEX.${s}`)}
                        </option>
                    ))}
                </select>
            </div>

            <div className={styles.formGroup}>
                <label htmlFor="email">{t('player.email')}</label>
                <input
                    id="email"
                    className={styles.input}
                    {...register('email', { setValueAs: (v) => (v === '' ? null : v) })}
                />
            </div>

            <div className={styles.formGroup}>
                <label>{t('player.language')}</label>
                <LanguageSelector value={watch('language') ?? 'fr'} onChange={(lang) => setValue('language', lang)} />
            </div>

            <div className={styles.actionsContainer}>
                <button type="button" className={styles.deleteButton} onClick={() => navigate('/players')}>
                    {t('player.cancel.button')}
                </button>

                <button type="submit">{player ? t('player.update.button') : t('player.create.button')}</button>
            </div>
        </form>
    );
};

export default PlayerPage;
